import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

export default function ProjectConsumption() {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [consumptions, setConsumptions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectStock, setProjectStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Customer Autocomplete State
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Form State
  const [projectId, setProjectId] = useState('');
  const [items, setItems] = useState([
    { inventoryItemId: '', quantity: '', notes: '', searchText: '', dropdownOpen: false }
  ]);

  // Edit State
  const [editingConsumption, setEditingConsumption] = useState(null);
  const [editFormData, setEditFormData] = useState({ quantity: '', notes: '' });
  const [editAvailableStock, setEditAvailableStock] = useState(0);

  useEffect(() => {
    fetchConsumptions();
    fetchCustomers();
  }, []);

  const fetchConsumptions = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/inventory/consumptions');
      setConsumptions(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load consumption records.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get('/customers?size=1000');
      const data = res.data.data || res.data || {};
      const list = data.content || (Array.isArray(data) ? data : []);
      setCustomers(list);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCustomerSearchChange = (val) => {
    setCustomerSearch(val);
    setShowCustomerDropdown(true);
    if (!val) {
      setSelectedCustomer(null);
      setProjects([]);
      setProjectId('');
      setItems([{ inventoryItemId: '', quantity: '', notes: '', searchText: '', dropdownOpen: false }]);
      setProjectStock([]);
    }
  };

  const handleSelectCustomer = async (c) => {
    setSelectedCustomer(c);
    setCustomerSearch(c.name || c.companyName || '');
    setShowCustomerDropdown(false);
    
    // Load projects associated with the selected customer
    try {
      const res = await axios.get(`/projects/by-customer/${c.id}`);
      const data = res.data.data || res.data || [];
      setProjects(data);
    } catch (e) {
      console.error(e);
      setProjects([]);
      toast.error('Failed to fetch projects for selected customer.');
    }
    
    setProjectId('');
    setItems([{ inventoryItemId: '', quantity: '', notes: '', searchText: '', dropdownOpen: false }]);
    setProjectStock([]);
  };

  const handleProjectChange = async (selectedProjId) => {
    setProjectId(selectedProjId);
    setItems([{ inventoryItemId: '', quantity: '', notes: '', searchText: '', dropdownOpen: false }]);
    if (!selectedProjId) {
      setProjectStock([]);
      return;
    }
    try {
      const res = await axios.get(`/projects/${selectedProjId}/stock`);
      const data = res.data.data || res.data || [];
      setProjectStock(data);
    } catch (e) {
      console.error(e);
      setProjectStock([]);
      toast.error('Failed to fetch items in project stock.');
    }
  };

  const handleAddRow = () => {
    setItems(prev => [...prev, { inventoryItemId: '', quantity: '', notes: '', searchText: '', dropdownOpen: false }]);
  };

  const handleRemoveRow = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleRowSearchChange = (index, val) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return {
          ...row,
          searchText: val,
          inventoryItemId: val ? row.inventoryItemId : '',
          dropdownOpen: true
        };
      }
      return row;
    }));
  };

  const handleRowFocus = (index) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return { ...row, dropdownOpen: true };
      }
      return row;
    }));
  };

  const handleRowBlur = (index) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        const matched = projectStock.find(s => String(s.inventoryItemId) === String(row.inventoryItemId));
        const cleanSearch = matched ? `${matched.itemName} (${matched.itemCode})` : '';
        return { ...row, dropdownOpen: false, searchText: cleanSearch };
      }
      return row;
    }));
  };

  const handleSelectRowItem = (index, stock) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return {
          ...row,
          inventoryItemId: stock.inventoryItemId,
          searchText: `${stock.itemName} (${stock.itemCode})`,
          dropdownOpen: false,
          quantity: ''
        };
      }
      return row;
    }));
  };

  const handleRowQuantityChange = (index, quantity) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return { ...row, quantity };
      }
      return row;
    }));
  };

  const handleRowNotesChange = (index, notes) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return { ...row, notes };
      }
      return row;
    }));
  };

  const handleOpenEdit = async (c) => {
    setEditingConsumption(c);
    setEditFormData({
      quantity: c.quantity,
      notes: c.notes || ''
    });
    
    try {
      const res = await axios.get(`/projects/${c.projectId}/stock`);
      const data = res.data.data || res.data || [];
      const stockItem = data.find(x => String(x.inventoryItemId) === String(c.inventoryItemId));
      setEditAvailableStock(stockItem ? parseFloat(stockItem.quantity) + parseFloat(c.quantity) : parseFloat(c.quantity));
    } catch (e) {
      console.error(e);
      setEditAvailableStock(parseFloat(c.quantity));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(editFormData.quantity) > editAvailableStock) {
      toast.error(`Cannot consume more than available project stock (${editAvailableStock} ${editingConsumption.unitOfMeasurement}).`);
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`/inventory/consumptions/${editingConsumption.id}`, {
        quantity: parseFloat(editFormData.quantity),
        notes: editFormData.notes
      });
      toast.success('Consumption record updated successfully.');
      setEditingConsumption(null);
      fetchConsumptions();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to update consumption record.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (movementId) => {
    setIdToDelete(movementId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteConfirmOpen(false);
    if (!idToDelete) return;
    try {
      await axios.delete(`/inventory/consumptions/${idToDelete}`);
      toast.success('Consumption record deleted successfully.');
      fetchConsumptions();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to delete consumption record.');
    } finally {
      setIdToDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId) {
      toast.error('Please select a project.');
      return;
    }

    if (items.some(x => !x.inventoryItemId || !x.quantity)) {
      toast.error('Please fill all required item and quantity fields.');
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      const stockItem = projectStock.find(x => String(x.inventoryItemId) === String(row.inventoryItemId));
      if (stockItem && parseFloat(row.quantity) > parseFloat(stockItem.quantity)) {
        toast.error(`Insufficient stock for ${stockItem.itemName}. Available: ${stockItem.quantity} ${stockItem.unitOfMeasurement}, Entered: ${row.quantity}`);
        return;
      }
    }

    setSubmitting(true);
    
    const payload = {
      projectId,
      items: items.map(x => ({
        inventoryItemId: x.inventoryItemId,
        quantity: parseFloat(x.quantity),
        notes: x.notes
      }))
    };

    try {
      await axios.post('/inventory/consumptions', payload);
      toast.success('Batch project material consumption recorded successfully.');
      setProjectId('');
      setSelectedCustomer(null);
      setCustomerSearch('');
      setItems([{ inventoryItemId: '', quantity: '', notes: '', searchText: '', dropdownOpen: false }]);
      setProjectStock([]);
      fetchConsumptions();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to record consumption.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const query = customerSearch.toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(query)) ||
      (c.companyName && c.companyName.toLowerCase().includes(query))
    );
  });

  return (
    <div className="container" style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Project Material Consumption</h1>
      </div>



      <div style={{ display: 'flex', flexDirection: 'column', gap: '25px', width: '100%' }}>
        {/* Record Consumption Form */}
        <div className="transaction-form-container" style={{ width: '100%' }}>
          <h2 className="section-title">Record Material Consumption</h2>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            
            {/* Searchable Customer Input */}
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Customer *</label>
              <input
                type="text"
                placeholder="Type to search customer..."
                value={customerSearch}
                onChange={e => handleCustomerSearchChange(e.target.value)}
                onFocus={() => setShowCustomerDropdown(true)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 250)}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <ul style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 10,
                  margin: '4px 0 0 0',
                  padding: '5px 0',
                  listStyle: 'none',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  {filteredCustomers.map(c => (
                    <li 
                      key={c.id}
                      onClick={() => handleSelectCustomer(c)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        fontSize: '13px'
                      }}
                      onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                      onMouseLeave={e => e.target.style.background = 'transparent'}
                    >
                      <strong>{c.name}</strong> {c.companyName ? `(${c.companyName})` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Project Selection (Dependent on Customer) */}
            <div className="form-group">
              <label>Project *</label>
              <select
                value={projectId}
                onChange={e => handleProjectChange(e.target.value)}
                disabled={!selectedCustomer}
                required
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">
                  {!selectedCustomer ? '-- Select Customer First --' : projects.length === 0 ? '-- No Projects Found --' : '-- Select Project --'}
                </option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Material Items List</span>
                <button
                  type="button"
                  onClick={handleAddRow}
                  disabled={!projectId}
                  className="btn-secondary"
                  style={{ padding: '4px 10px', fontSize: '12px' }}
                >
                  + Add Product
                </button>
              </div>

              {items.map((row, index) => {
                const selectedStockItem = projectStock.find(x => String(x.inventoryItemId) === String(row.inventoryItemId));
                return (
                  <div key={index} style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    alignItems: 'flex-start', 
                    padding: '10px', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '6px',
                    marginBottom: '10px',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div className="form-group" style={{ flex: 2, margin: 0, position: 'relative' }}>
                      <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Product *</label>
                      <input
                        type="text"
                        placeholder={projectId ? "Type to search product..." : "-- Choose Project First --"}
                        value={row.searchText || ''}
                        onChange={e => handleRowSearchChange(index, e.target.value)}
                        onFocus={() => handleRowFocus(index)}
                        onBlur={() => setTimeout(() => handleRowBlur(index), 250)}
                        required
                        disabled={!projectId}
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                      />
                      {row.dropdownOpen && projectStock.length > 0 && (
                        <ul style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: '#fff',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          maxHeight: '150px',
                          overflowY: 'auto',
                          zIndex: 100,
                          margin: '4px 0 0 0',
                          padding: '5px 0',
                          listStyle: 'none',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                          {projectStock
                            .filter(stock => {
                              const q = (row.searchText || '').toLowerCase();
                              return (stock.itemName || '').toLowerCase().includes(q) || (stock.itemCode || '').toLowerCase().includes(q);
                            })
                            .map(stock => {
                              const isSelectedElsewhere = items.some((x, i) => i !== index && String(x.inventoryItemId) === String(stock.inventoryItemId));
                              return (
                                <li
                                  key={stock.inventoryItemId}
                                  onClick={() => {
                                    if (!isSelectedElsewhere) {
                                      handleSelectRowItem(index, stock);
                                    }
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: isSelectedElsewhere ? 'not-allowed' : 'pointer',
                                    borderBottom: '1px solid #f3f4f6',
                                    fontSize: '12px',
                                    color: isSelectedElsewhere ? '#9ca3af' : 'inherit',
                                    backgroundColor: isSelectedElsewhere ? '#f9fafb' : 'transparent'
                                  }}
                                  onMouseEnter={e => { if (!isSelectedElsewhere) e.target.style.background = '#f3f4f6'; }}
                                  onMouseLeave={e => { if (!isSelectedElsewhere) e.target.style.background = isSelectedElsewhere ? '#f9fafb' : 'transparent'; }}
                                >
                                  <strong>{stock.itemName}</strong> ({stock.itemCode}) {isSelectedElsewhere ? '[Selected]' : ''}
                                </li>
                              );
                            })}
                        </ul>
                      )}
                      {selectedStockItem && (
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                          Avail: <strong>{selectedStockItem.quantity} {selectedStockItem.unitOfMeasurement}</strong>
                        </div>
                      )}
                    </div>

                    <div className="form-group" style={{ flex: 1, margin: 0 }}>
                      <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Qty *</label>
                      <input
                        type="number"
                        step="any"
                        min="0.001"
                        placeholder="Qty"
                        value={row.quantity}
                        onChange={e => handleRowQuantityChange(index, e.target.value)}
                        disabled={!row.inventoryItemId}
                        required
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                      />
                    </div>

                    <div className="form-group" style={{ flex: 2, margin: 0 }}>
                      <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Usage Notes</label>
                      <input
                        type="text"
                        placeholder="Notes"
                        value={row.notes}
                        onChange={e => handleRowNotesChange(index, e.target.value)}
                        disabled={!row.inventoryItemId}
                        style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                      />
                    </div>

                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(index)}
                        style={{
                          alignSelf: 'center',
                          marginTop: '15px',
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: '0 5px'
                        }}
                        title="Remove Row"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || items.some(x => !x.inventoryItemId)}
              style={{ width: '100%', padding: '10px', fontWeight: 'bold', marginTop: '10px' }}
            >
              {submitting ? 'Recording...' : 'Record Consumption'}
            </button>
          </form>
        </div>

        {/* Consumption History Table */}
        <div className="transaction-form-container" style={{ width: '100%' }}>
          <h2 className="section-title">Consumption Log History</h2>
          {loading ? (
            <div>Loading history...</div>
          ) : consumptions.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
              No material consumption records found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th>Date</th>
                    <th>Project</th>
                    <th>Item</th>
                    <th style={{ textAlign: 'right' }}>Consumed Qty</th>
                    <th>Notes</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {consumptions.map(c => (
                    <tr key={c.id}>
                      <td>{new Date(c.movementDate).toLocaleDateString()} {new Date(c.movementDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                      <td><strong>{c.projectName || '—'}</strong></td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{c.itemName}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{c.itemCode}</div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#ef4444' }}>
                        {c.quantity} {c.unitOfMeasurement}
                      </td>
                      <td>{c.notes || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="btn-secondary" style={{ marginRight: '6px', padding: '2px 6px', fontSize: '11px' }} onClick={() => handleOpenEdit(c)}>Edit</button>
                        <button className="btn-danger" style={{ padding: '2px 6px', fontSize: '11px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} onClick={() => handleDelete(c.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingConsumption && (
        <div className="modal-overlay" style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} onClick={() => setEditingConsumption(null)}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '8px', padding: '20px', width: '90%', maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Edit Consumption Record</h3>
              <button style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }} onClick={() => setEditingConsumption(null)}>&times;</button>
            </div>
            
            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Project</label>
                <input type="text" readOnly value={editingConsumption.projectName || ''} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', background: '#f3f4f6' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Material Item</label>
                <input type="text" readOnly value={`${editingConsumption.itemName} (${editingConsumption.itemCode})`} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', background: '#f3f4f6' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Quantity to Consume *</label>
                <input 
                  type="number" 
                  step="any"
                  min="0.001"
                  required
                  value={editFormData.quantity} 
                  onChange={e => setEditFormData(prev => ({ ...prev, quantity: e.target.value }))} 
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                />
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                  Max Available (incl. current): <strong>{editAvailableStock} {editingConsumption.unitOfMeasurement}</strong>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Notes / Remarks</label>
                <textarea 
                  value={editFormData.notes} 
                  onChange={e => setEditFormData(prev => ({ ...prev, notes: e.target.value }))} 
                  rows={3}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical' }} 
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
                <button type="button" className="btn-secondary" onClick={() => setEditingConsumption(null)} style={{ padding: '8px 16px' }}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting} style={{ padding: '8px 16px', fontWeight: 'bold' }}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        title="Delete Consumption Record"
        message="Are you sure you want to delete this consumption record? This will return the material back to the project stock."
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setIdToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
